import React, { memo, useEffect } from 'react';
import { GridBase } from '../../components';
import strings from '../../globalization';
import { faTimes, faCheckCircle } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  trackingSystemContactList,
  trackingSystemContactFormReset
} from '../../redux/actions';
import Swal from 'sweetalert2';
import { useDispatch, useSelector } from 'react-redux';

const StatusIcon = ({ isActive }) => (
  <FontAwesomeIcon
    icon={isActive ? faCheckCircle : faTimes}
    style={{ color: isActive ? 'green' : 'red', fontSize: '20px' }}
  />
);

/**
 * Renders an icon representing the active state of a row.
 *
 * @component
 * @param {Object} props - Component props.
 * @param {Object} props.row - The row object containing data.
 * @param {boolean} props.row.IsActive - Indicates whether the row is active.
 * @param {string|number} props.row.id - Unique identifier for the row, used as the key.
 * @returns {JSX.Element} A centered icon: green check if active, red X if inactive.
 */
const Active = ({ row }) => {
  const { IsActive, id } = row;
  return (
    <div
      key={id}
      style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100%'
      }}
    >
      <StatusIcon isActive={IsActive} />
    </div>
  );
};

/**
 * Column configuration for the Tracking System Contact grid.
 *
 * @type {Array<Object>}
 */
const columns = [
  {
    key: 'ContactName',
    name: strings.ContactName,
    width: 250,
    type: 'text',
    displayValue: 'ContactName'
  },
  {
    key: 'CompanyName',
    name: strings.CompanyName,
    width: 250,
    type: 'text',
    displayValue: 'CompanyName'
  },
  {
    key: 'ContactEmail',
    name: strings.ContactEmail,
    width: 250,
    type: 'text',
    displayValue: 'ContactEmail'
  },
  {
    key: 'CompanyAddress1',
    name: strings.CompanyAddress1,
    width: 250,
    type: 'text',
    displayValue: 'CompanyAddress1'
  },
  {
    key: 'CompanyAddress2',
    name: strings.CompanyAddress2,
    width: 250,
    type: 'text',
    displayValue: 'CompanyAddress2'
  },
  {
    key: 'City',
    name: strings.City,
    width: 250,
    type: 'text',
    displayValue: 'City'
  },
  {
    key: 'State',
    name: strings.State,
    width: 250,
    type: 'text',
    displayValue: 'State'
  },
  {
    key: 'PostalCode',
    name: strings.PostalCode,
    width: 250,
    type: 'text',
    displayValue: 'PostalCode'
  },
  {
    key: 'PhoneNumber',
    name: strings.PhoneNumber,
    width: 250,
    type: 'text',
    displayValue: 'PhoneNumber'
  },
  {
    key: 'CountryCode',
    name: strings.CountryCode,
    width: 250,
    type: 'select',
    store: 'CountryCode',
    mode: 'remote',
    valueProp: '_id',
    displayValue: 'DisplayValue'
  },
  {
    key: 'IsActive',
    name: strings.Active,
    type: 'bool',
    getRowMetaData: row => row,
    formatter: Active,
    width: 150,
    cellClass: 'cell-center',
    headerCellClass: 'cell-center'
  }
];

/**
 * Tracking System Contact grid component.
 *
 * Displays a list of contacts with options to delete entries.
 *
 * @component
 * @param {Object} props - Props passed to the component.
 * @returns {JSX.Element} Grid component with contact data.
 */
const TrackingSystemContact = memo(props => {
  const dispatch = useDispatch();
  const { CustomError: customError, err, info, success } = useSelector(
    state => state.trackingSystemContactFormData.data
  );

  useEffect(() => {
    if (customError && err === 'DeleteFailed') {
      Swal.fire({
        title: strings[err],
        text: strings.ContactLinkedToTrackingSystem,
        icon: 'warning'
      });
      dispatch(trackingSystemContactFormReset());
    }
  }, [customError, err, dispatch]);

  useEffect(() => {
    if (info === 'DeleteSuccess' && success) {
      dispatch(
        trackingSystemContactList({
          filters: props.baseFilter || {},
          action: 'list',
          limit: 25,
          skip: 0,
          sort: { sort: -1 },
          comboType: ['CountryCode']
        })
      );
      dispatch(trackingSystemContactFormReset());
    }
  }, [info, success, dispatch, props.baseFilter]);

  return (
    <GridBase
      {...props}
      disableShowHideColumns={false}
      disableFilter={false}
      columns={columns}
      title={strings.TrackingSystemContacts}
      dataProperty="trackingSystemContactListData"
      parentRoute="trackingSystemContact"
      action={trackingSystemContactList}
      comboType={['CountryCode']}
      sortColumn="ContactName"
      gridSortDirection="ASC"
      style={{ flex: 1 }}
      loadPref={true}
      listId="TrackingSystemContact"
    />
  );
});

export default TrackingSystemContact;
